import { parseISO } from 'date-fns';

import {
    mapBudget,
    mapInstitution,
    mapLanguageCodes,
    mapMatchTopics,
    mapPrinciples,
    mapUser,
    Match,
    MatchingConfig,
    MatchingResultContactStatus,
    MatchStatus,
    RMatching,
    RMatchingConfig
} from '@dh/types';

export const mapMatchingConfig = (config: RMatchingConfig): MatchingConfig => {
    return {
        weight: {
            topic: config.weight.topic,
            principle: config.weight.principle
        },
        topic: {
            displayPercentage: config.topicDisplayPercentage,
            itemDisplayPercentage: config.topicItemDisplayPercentage,
            threshold: config.topicThreshold
        },
        principle: {
            displayPercentage: config.principleDisplayPercentage
        },
        matching: {
            displayPercentage: config.matchingDisplayPercentage
        }
    };
};

export const mapMatching = (match: RMatching): Match => {
    // sort the company matches by their score
    const companyMatches =
        match.matches
            // we assume that the last element is the most recent (published) one
            .at(-1)
            ?.matches // and we want them to be sorted by score (highest score first
            .sort((a, b) => b.result.total - a.result.total) ??
        // but when we do not have any matches, we return an empty array
        [];

    return {
        // strapi document attributes and timestamps
        id: match.id,
        documentId: match.documentId,
        createdAt: parseISO(match.createdAt),
        updatedAt: parseISO(match.updatedAt),
        publishedAt: match.publishedAt ? parseISO(match.publishedAt) : null,

        // custom attributes
        title: match.title,
        description: match.description,
        issuerInstitution: mapInstitution(match.owner),
        status: match.themeStatus as MatchStatus,

        updatedBy: match.last_changed_by ? mapUser(match.last_changed_by) : undefined,

        contact: match.contact ? mapUser(match.contact) : undefined,

        matchedInstitutions: companyMatches.map((m) => ({
            institution: mapInstitution(m.company),
            score: {
                total: m.result.total,
                topics: m.result.topics,
                principles: m.result.principles
            },
            isNew: m.isNew,
            contactStatus: m.contactStatus as MatchingResultContactStatus
        })),

        startDate: match.createdAt ? parseISO(match.createdAt) : undefined,
        endDate: match?.endAt ? parseISO(match.endAt) : undefined,

        conditions: {
            dateOfFinalization: match?.constraints?.date_of_finalization
                ? parseISO(match.constraints.date_of_finalization)
                : undefined,
            geographicRadius: match?.constraints?.geographic_radius,
            geographicAddress:
                !!match?.constraints?.geographic_address &&
                !!match?.constraints?.geographic_address_longitude &&
                !!match?.constraints?.geographic_address_latitude
                    ? {
                          fullAddress: match.constraints.geographic_address,
                          coordinates: [
                              match.constraints.geographic_address_longitude,
                              match.constraints.geographic_address_latitude
                          ]
                      }
                    : undefined,
            languages: mapLanguageCodes(match?.constraints?.languages),
            budget: match?.constraints?.budget ? mapBudget(match.constraints.budget) : undefined
        },
        principles: mapPrinciples(match.principles),
        topics: mapMatchTopics({ topics: match.topics, doubledTopics: match.scored_topics })
    };
};

export const mapMatchings = (matches: RMatching[] | undefined): Match[] =>
    matches ? matches.map(mapMatching) : [];
